import React from 'react';

interface CardProps {
  children: React.ReactNode;
}

export function TabSection({ children }: CardProps) {
  return (
    <div className="mb-5 animate-[fadeIn_0.4s_ease-in-out] rounded-xl border border-slate-200/50 bg-white p-7 shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
      {children}
    </div>
  );
}

interface SectionHeaderProps {
  title: React.ReactNode;
}

export function SectionHeader({ title }: SectionHeaderProps) {
  return (
    <div className="-mx-7 -mt-7 mb-6 rounded-t-xl bg-rgpd-blue py-4 text-center font-semibold text-white uppercase">
      {title}
    </div>
  );
}

interface HighlightProps {
  children: React.ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'neutral';
}

const highlightClasses: Record<string, string> = {
  info: 'border-l-4 border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 text-slate-700',
  success: 'border-l-4 border-green-500 bg-gradient-to-br from-green-50 to-green-100 text-green-900',
  warning: 'border-l-4 border-amber-400 bg-gradient-to-br from-amber-50 to-amber-100 text-amber-800',
  neutral: 'border-l-4 border-slate-400 bg-gradient-to-br from-gray-50 to-gray-100 text-slate-700',
};

export function HighlightBox({ children, variant = 'info' }: HighlightProps) {
  return (
    <div className={`my-5 rounded-lg p-4 ${highlightClasses[variant]}`}>
      {children}
    </div>
  );
}

interface SubsectionProps {
  children: React.ReactNode;
  bordered?: boolean;
}

export function Subsection({ children, bordered = true }: SubsectionProps) {
  return (
    <div className={`mb-6 rounded-lg bg-gray-50 p-5 ${bordered ? 'border-l-[3px] border-gray-500' : ''}`}>
      {children}
    </div>
  );
}

interface WarningBoxProps {
  title?: React.ReactNode;
  children: React.ReactNode;
}

export function WarningBox({ title, children }: WarningBoxProps) {
  return (
    <div className="my-5 rounded-lg border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-amber-100 p-5 text-amber-800">
      {title && <h4 className="mb-3 font-bold">{title}</h4>}
      {children}
    </div>
  );
}

interface SuccessBoxProps {
  title?: React.ReactNode;
  children: React.ReactNode;
}

export function SuccessBox({ title, children }: SuccessBoxProps) {
  return (
    <div className="my-5 rounded-lg border-2 border-green-500 bg-gradient-to-br from-green-50 to-green-100 p-5 text-green-900">
      {title && <h4 className="mb-3 font-bold">{title}</h4>}
      {children}
    </div>
  );
}

interface StatsCardProps {
  value: React.ReactNode;
  label: React.ReactNode;
}

export function StatsCard({ value, label }: StatsCardProps) {
  return (
    <div className="mb-5 rounded-xl border-t-4 border-red-500 bg-white p-5 text-center shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
      <div className="mb-2 text-3xl font-bold text-red-500">{value}</div>
      <div className="text-sm leading-snug text-gray-500">{label}</div>
    </div>
  );
}

interface TableProps {
  children: React.ReactNode;
}

export function StyledTable({ children }: TableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="my-5 w-full border-collapse bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
        {children}
      </table>
    </div>
  );
}

interface ScriptBoxProps {
  children: React.ReactNode;
}

export function ScriptBox({ children }: ScriptBoxProps) {
  return (
    <div className="my-5 rounded-lg border-2 border-gray-200 bg-white p-6 font-mono text-[0.95rem] leading-[1.8]">
      {children}
    </div>
  );
}
